import { Assert } from "../../Assert";
import { PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, JNative } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyString } from "../../RadObject";
import { WebApiClientService } from "../../Server/WebApiClientService";
import { ServiceManager } from "../../ServiceManager";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { NAMESPACE } from "./_Namespace";

export type TextApplicationEntityPropertyValues =
    InputControlPropertyValues
    & {
        primaryEntity: string;
        primaryEntityDescriptionProperty: string;
    };

export type TextApplicationEntityProperties = {
    [name in keyof TextApplicationEntityPropertyValues]: Property<TextApplicationEntityPropertyValues[name]>
};

export interface ITextApplicationEntity extends IInputControl, TextApplicationEntityProperties
{
}

export class TextApplicationEntity extends InputControl implements ITextApplicationEntity
{
    public static override classNamespace = NAMESPACE + "TextApplicationEntity";
    public static override className = "TextApplicationEntity";

    // #region Global Variables

    protected $$valueElement?: JNative<HTMLElement>;

    // #endregion

    // #region Properties

    public primaryEntity!: PropertyString;
    public primaryEntityDescriptionProperty!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TextApplicationEntityPropertyValues>)
    {
        super(propertyValues);
    }

    // #endregion

    // #region Public Methods

    public async loadDataAsync(): Promise<void>
    {
        const
            dataSource = this.dataSource.get(),
            primaryEntity = this.primaryEntity.get(),
            primaryEntityDescriptionProperty = this.primaryEntityDescriptionProperty.get(),
            dataSourceProperty = this.dataSourceProperty.get(),
            entityId = dataSource && dataSourceProperty ? dataSource.propertyValueByPathGet(dataSourceProperty) : null;

        Assert.isNotNullOrUndefined(primaryEntity, "primaryEntity");
        Assert.isNotNullOrUndefined(primaryEntityDescriptionProperty, "primaryEntityDescriptionProperty");

        if (!entityId)
        {
            return new Promise(resolve =>
            {
                window.setTimeout(() =>
                {
                    this.text.set(null);
                    resolve(undefined);
                });
            });
        }
        const
            data =
                await ServiceManager.getRequired(WebApiClientService)
                    .createEntityGetRequest(
                        primaryEntity,
                        entityId,
                        [
                            "id",
                            primaryEntityDescriptionProperty
                        ],
                        { "RAD-Option-Include-Soft-Deleted": true }
                    )
                    .executeAsync();

        this.text.set(data ? data[primaryEntityDescriptionProperty] : null);
    }

    // #endregion

    // #region Protected Methods

    protected _primaryEntityChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _primaryEntityDescriptionPropertyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TextApplicationEntityPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.primaryEntity = new PropertyString(this, propertyValues.primaryEntity, this._primaryEntityChanged);
        this.primaryEntityDescriptionProperty = new PropertyString(this, propertyValues.primaryEntityDescriptionProperty, this._primaryEntityDescriptionPropertyChanged);
    }

    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.labelTargetId = this.controlId + "-p";
        this.$$valueElement = $$(StringHelper.formatWithHtmlEncode("<p id=\"{0}\"></p>", this.labelTargetId)).appendTo($$element);

        return $$element;
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);
        this.$$valueElement?.text(change.currentValue);
    }

    // #endregion
}

